import React, { useState, useEffect, useRef } from 'react';
import { useMarketStore } from './store/marketStore';
import ChartView from './components/ChartView';
import ErrorBoundary from './components/ErrorBoundary';
import ChartErrorFallback from './components/ChartErrorFallback';
import PasscodeGate from './components/PasscodeGate';
import { ToastContainer } from './components/Toast';
import { ToastHistoryPanel } from './components/ToastHistoryPanel';
import { RMSUnlockModal, DisciplineSettingsPanel } from './components/RMS';
import { wsService } from './services/WebSocketService';
import { isMarketOpen } from './utils/marketHours';
import { getApiBaseUrl } from './config/ports';
import { apiFetch } from './services/api';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useSymbolStore } from './store/symbolStore';
import { resolveActiveSymbol, contractBase } from './utils/symbolUtils';
import { symbolFromHash } from './components/TradeFinder/tradeFinderUx';
import NewsWarningEffect from './components/NewsWarningEffect';
import './App.css';

// Get default symbol from registry for fallbacks
const getDefaultSymbol = (): string => {
  const symbols = useSymbolStore.getState().symbols;
  if (symbols.length > 0) {
    return symbols[0].fullSymbol;
  }
  return 'MNQU6.CME'; // Ultimate fallback if registry is empty
};

const SYMBOL_STORAGE_KEY = 'selectedSymbol';
let appContentInstanceCounter = 0;

const AppContent: React.FC = () => {
  const instanceIdRef = useRef(++appContentInstanceCounter);
  // Symbol will be fetched from backend on connect, or restored from localStorage
  const [symbol, setSymbolState] = useState('');
  const [, setSymbolsLoading] = useState(true);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const connectionStatus = useMarketStore((state) => state.connectionStatus);
  const marketStatus = useMarketStore((state) => state.marketStatus);
  const setMarketOpen = useMarketStore((state) => state.setMarketOpen);

  // Update document title with PnL and environment
  useDocumentTitle();

  // Version check for auto-refresh when new version deployed
  useVersionCheck();

  // Removed: a click-count gesture on the root element that called forceRefresh().
  // It fired during normal chart interaction (rapid clicks are routine here) and
  // force-refresh is destructive - it clears caches, unregisters the service
  // worker and hard-reloads mid-session.

  // Wrapper to persist symbol to localStorage
  const setSymbol = (newSymbol: string) => {
    console.log(`📊 [APP ${instanceIdRef.current}] setSymbol`, { from: symbol, to: newSymbol });
    setSymbolState(newSymbol);
    if (newSymbol) {
      localStorage.setItem(SYMBOL_STORAGE_KEY, newSymbol);
      console.log('💾 [APP] Saved symbol to localStorage:', newSymbol);
    }
  };

  useEffect(() => {
    console.log(`🧩 [APP ${instanceIdRef.current}] mounted`, {
      hostname: window.location.hostname,
      port: window.location.port,
    });
    return () => {
      console.log(`🧩 [APP ${instanceIdRef.current}] unmounted`);
    };
  }, []);

  // Fetch available symbols from backend on mount, but prefer localStorage
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const response = await apiFetch(`${getApiBaseUrl()}/symbols`);
        const data = await response.json();
        // API returns { symbols: [...], availableSymbols: [...], primarySymbol: ... }
        const activeSymbols: string[] = data.symbols || [];
        useSymbolStore.getState().setActiveSymbols(activeSymbols);

        // Check localStorage first for persisted symbol
        const savedSymbol = localStorage.getItem(SYMBOL_STORAGE_KEY);
        console.log('📊 [APP] Checking symbols - saved:', savedSymbol, 'active:', activeSymbols);

        // One resolver, shared with SymbolSelector and WebSocketService. A
        // saved symbol from last quarter re-maps to this quarter's contract;
        // an unknown one falls back to something the engine is actually
        // streaming rather than being handed back verbatim.
        const resolved = resolveActiveSymbol(savedSymbol, activeSymbols);
        // A deep link (`/#MNQ`, e.g. from a TradeFinder push) wins over the saved symbol.
        const linked = symbolFromHash(window.location.hash, activeSymbols, contractBase);

        if (linked) {
          setSymbol(linked);
          console.log('📊 [APP] Symbol from link:', window.location.hash, '->', linked);
        } else if (resolved && resolved === savedSymbol) {
          setSymbolState(savedSymbol);
          console.log('📊 [APP] Restored symbol from localStorage:', savedSymbol);
        } else if (resolved) {
          setSymbol(resolved);
          console.log('📊 [APP] Resolved symbol to active contract:', savedSymbol, '->', resolved);
        } else {
          const defaultSym = getDefaultSymbol();
          setSymbol(defaultSym);
          console.log('📊 [APP] No active symbols, using default:', defaultSym);
        }
      } catch (error) {
        console.error('❌ [APP] Failed to fetch symbols from backend:', error);
        // Try localStorage fallback
        const savedSymbol = localStorage.getItem(SYMBOL_STORAGE_KEY);
        if (savedSymbol) {
          setSymbolState(savedSymbol);
          console.log('📊 [APP] Using saved symbol after API failure:', savedSymbol);
        } else {
          setSymbol(getDefaultSymbol());
        }
      } finally {
        setSymbolsLoading(false);
      }
    };
    fetchSymbols();
  }, []);

  // Track if we've connected
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    // Don't connect until we have a symbol
    if (!symbol) return;

    console.log(`🔌 [APP ${instanceIdRef.current}] connect effect`, {
      symbol,
      hasConnected: hasConnectedRef.current,
      wsConnected: wsService.isConnected(),
    });

    // Only connect once - symbol changes should NOT trigger reconnect
    if (!hasConnectedRef.current) {
      hasConnectedRef.current = true;
      wsService.connect();
    }

    // Cleanup only on unmount
    return () => {
      // Only disconnect if we're actually unmounting (not just symbol changing)
      // This is called on every re-render of the effect, but we only want to
      // disconnect when the component truly unmounts
    };
  }, [symbol]);

  // Separate effect for disconnect on unmount
  useEffect(() => {
    return () => {
      console.log(`🔌 [APP ${instanceIdRef.current}] cleanup disconnect`);
      wsService.disconnect();
    };
  }, []);

  // Subscribe to symbol (subscription will be queued if not connected yet)
  useEffect(() => {
    if (!symbol) return;
    console.log(`[APP ${instanceIdRef.current}] subscribe effect`, {
      symbol,
      wsConnected: wsService.isConnected(),
    });
    // Sync market store with the active symbol so WS handlers
    // route incoming data correctly (symbol filter guard)
    if (useMarketStore.getState().symbol !== symbol) {
      useMarketStore.getState().switchSymbol(symbol);
    }
    wsService.subscribe(symbol);
  }, [symbol]);

  // Check market hours every minute
  useEffect(() => {
    const checkMarketHours = () => {
      const isOpen = isMarketOpen();
      setMarketOpen(isOpen);
    };

    // Initial check
    checkMarketHours();

    // Check every minute
    const interval = setInterval(checkMarketHours, 60000);

    return () => clearInterval(interval);
  }, [setMarketOpen]);

  const handleSymbolChange = (newSymbol: string) => {
    console.log(`🔁 [APP ${instanceIdRef.current}] handleSymbolChange`, {
      from: symbol,
      to: newSymbol,
    });
    wsService.unsubscribe(symbol);
    useMarketStore.getState().switchSymbol(newSymbol);
    setSymbol(newSymbol);
    wsService.subscribe(newSymbol);
  };

  // A deep link opened while the app is already up (`/#ES` in the same tab).
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;
  useEffect(() => {
    const onHash = () => {
      const active = useSymbolStore.getState().activeSymbols;
      const linked = symbolFromHash(window.location.hash, active, contractBase);
      const current = symbolRef.current;
      if (!linked || !current || linked === current) return;
      wsService.unsubscribe(current);
      useMarketStore.getState().switchSymbol(linked);
      setSymbol(linked);
      wsService.subscribe(linked);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBacktestClick = () => {
    setShowBacktestModal(true);
  };

  const handleBacktestModalClose = () => {
    setShowBacktestModal(false);
  };

  return (
    <div className="app">
      <main className="app-main">
        <ErrorBoundary
          name="ChartView"
          fallback={<ChartErrorFallback error={null} onRetry={() => window.location.reload()} />}
        >
          <ChartView
            onSymbolChange={handleSymbolChange}
            showBacktestModal={showBacktestModal}
            onBacktestModalClose={handleBacktestModalClose}
            onBacktestClick={handleBacktestClick}
            connectionStatus={connectionStatus}
            marketStatus={marketStatus}
          />
        </ErrorBoundary>
      </main>
    </div>
  );
};

// Wrap app with PasscodeGate for PIN protection
const App: React.FC = () => {
  // Initialize Eruda mobile console for debugging
  useEffect(() => {
    const initEruda = async () => {
      try {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isStaging = window.location.port === '4002' || window.location.hostname.includes('staging');

        if (isMobile || isStaging) {
          // Create a dedicated container for Eruda to prevent it from affecting body styles
          let erudaContainer = document.getElementById('eruda-container');
          if (!erudaContainer) {
            erudaContainer = document.createElement('div');
            erudaContainer.id = 'eruda-container';
            document.body.appendChild(erudaContainer);
          }

          const eruda = await import('eruda');
          eruda.default.init({
            container: erudaContainer,
            tool: ['console', 'network', 'elements'],
            useShadowDom: false,
            autoScale: true,
          });

          // Remove any 'all: initial' style that Eruda might add to body
          if (document.body.style.all === 'initial') {
            document.body.style.all = '';
          }

          // Make button small and non-invasive via style injection
          const style = document.createElement('style');
          style.id = 'eruda-custom-style';
          style.textContent = `
            .eruda-entry-btn {
              bottom: 100px !important;
              right: -5px !important;
              width: 24px !important;
              height: 24px !important;
              opacity: 0.2 !important;
              transform: scale(0.6) !important;
              padding: 0 !important;
            }
            .eruda-entry-btn .eruda-icon {
              width: 20px !important;
              height: 20px !important;
            }
          `;
          document.head.appendChild(style);
        }
      } catch (err) {
        console.error('[Eruda] Failed to initialize:', err);
      }
    };
    initEruda();
  }, []);

  return (
    <>
      <ToastContainer />
      <ToastHistoryPanel />
      <RMSUnlockModal />
      <NewsWarningEffect />
      <DisciplineSettingsPanel />
      <PasscodeGate>
        <AppContent />
      </PasscodeGate>
    </>
  );
};

export default App;
